import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import type { Application, Tag, User } from '../api';
import { useSuperuserSession } from '../auth';
import { Banner, CredentialCallout, Field, Modal, Spinner, TagEditor, TagList } from '../components/ui';

export function ApplicationDetail() {
  const { token } = useSuperuserSession();
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState<Application | null>(null);
  const [masterUsers, setMasterUsers] = useState<User[]>([]);
  const [counts, setCounts] = useState({ users: 0, roles: 0, permissions: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reset, setReset] = useState<{ email: string; password: string; reason?: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getApplication(token, applicationId);
      setApplication(res.application);
      setMasterUsers(res.masterUsers);
      setCounts(res.counts);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the application');
    } finally {
      setLoading(false);
    }
  }, [token, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onResetMaster() {
    setError(null);
    setNotice(null);
    setReset(null);
    try {
      const res = await api.resetMasterPassword(token, applicationId);
      if (res.temporaryPassword && masterUsers[0]) {
        setReset({
          email: masterUsers[0].email,
          password: res.temporaryPassword,
          reason: res.emailDelivery.error,
        });
      } else {
        setNotice(`New credentials emailed to ${masterUsers[0]?.email ?? 'the master user'}.`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset the master password');
    }
  }

  if (loading) return <Spinner />;
  if (!application) {
    return (
      <div className="stack">
        <Banner kind="error">{error ?? 'Application not found'}</Banner>
        <Link className="link" to="/admin">
          ← Back to applications
        </Link>
      </div>
    );
  }

  return (
    <div className="stack">
      <Link className="link small" to="/admin">
        ← Applications
      </Link>

      <div className="page-head">
        <div>
          <h1>{application.name}</h1>
          <p className="muted small">
            Application ID <code>{application.applicationId}</code>
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button type="button" className="btn btn--danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        </div>
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}
      {notice && <Banner kind="success" onDismiss={() => setNotice(null)}>{notice}</Banner>}
      {reset && <CredentialCallout email={reset.email} password={reset.password} reason={reset.reason} />}

      <div className="stat-row">
        <div className="stat">
          <span className="stat__value">{counts.users}</span>
          <span className="stat__label">Users</span>
        </div>
        <div className="stat">
          <span className="stat__value">{counts.roles}</span>
          <span className="stat__label">Roles</span>
        </div>
        <div className="stat">
          <span className="stat__value">{counts.permissions}</span>
          <span className="stat__label">Permissions</span>
        </div>
      </div>

      <section className="card">
        <h2 className="card__title">Tags</h2>
        <TagList tags={application.tags} />
      </section>

      <section className="card">
        <div className="page-head">
          <h2 className="card__title">Master accounts</h2>
          <button type="button" className="btn btn--small" onClick={onResetMaster}>
            Reset &amp; resend credentials
          </button>
        </div>
        <p className="muted small">
          Master users manage this application's permissions, roles and users. Day-to-day
          administration happens in the application console — sign in there as a master user.
        </p>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Email verified</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {masterUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    {u.emailVerified ? (
                      <span className="pill pill--ok">Verified</span>
                    ) : (
                      <span className="pill pill--warn">Pending</span>
                    )}
                  </td>
                  <td className="muted small">{new Date(u.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <EditApplicationModal
          token={token}
          application={application}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {confirmDelete && (
        <DeleteApplicationModal
          token={token}
          application={application}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => navigate('/admin')}
        />
      )}
    </div>
  );
}

function EditApplicationModal({
  token,
  application,
  onClose,
  onSaved,
}: {
  token: string;
  application: Application;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(application.name);
  const [tags, setTags] = useState<Tag[]>(application.tags);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.updateApplication(token, application.applicationId, {
        name: name.trim(),
        tags: tags.filter((t) => t.key.trim()),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save changes');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Edit application" onClose={onClose} onSubmit={onSubmit} submitting={submitting}>
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Application name">
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Tags">
        <TagEditor value={tags} onChange={setTags} />
      </Field>
    </Modal>
  );
}

function DeleteApplicationModal({
  token,
  application,
  onClose,
  onDeleted,
}: {
  token: string;
  application: Application;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Typing the name is deliberate friction: this cascades to every user,
    // role and permission in the tenant and cannot be undone.
    if (confirmation !== application.name) {
      setError('Type the application name exactly to confirm');
      return;
    }
    setSubmitting(true);
    try {
      await api.deleteApplication(token, application.applicationId);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the application');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Delete application"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Delete permanently"
      submitting={submitting}
      destructive
    >
      {error && <Banner kind="error">{error}</Banner>}
      <Banner kind="warn">
        This permanently deletes <strong>{application.name}</strong> along with every user, role,
        permission and role assignment inside it.
      </Banner>
      <Field label={`Type “${application.name}” to confirm`}>
        <input
          className="input"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          autoComplete="off"
        />
      </Field>
    </Modal>
  );
}
